import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useSendLogoutToAuthMachine } from '@order/AuthMachine';
import { useLogoutMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import type { useCustomerData } from './useCustomerData';
import type { useSignUp } from './useSignUp';

export const useSignOut = (props: UseSignOutProps) => {
  const { customer, resetSignUpData } = props;

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const sendLogoutAction = useSendLogoutToAuthMachine();

  const [signOutResponse, signOutMutation] = useLogoutMutation();
  const signOutResponseData = signOutResponse.data?.logout;

  const isSigningOut = signOutResponse.fetching;
  const isSignedOut = !customer?.id;

  // ─────────────────────── Telemetry ──────────────────────────────────

  const { track } = useTelemetry();

  // ────────────────────────────────────────────────────────────────────

  const signOut = useCallback(async () => {
    if (!customer?.id) return;

    const response = await signOutMutation({
      input: { customerId: customer.id },
    });
    const logoutData = response?.data?.logout;
    const hasError =
      logoutData?.__typename === 'ValidationError' || response?.error;

    if (!logoutData || hasError) {
      const userError = t('general.error');
      const systemError = getUrqlError(response.error);

      track('logout.failure', { userError, systemError });
      addNoticeBanner({ text: userError, palette: 'caution' });

      return;
    }

    track('logout.success');
    track('account.logged_out_view');
    resetSignUpData();
    sendLogoutAction(logoutData?.csrf);
  }, [
    customer?.id,
    signOutMutation,
    resetSignUpData,
    sendLogoutAction,
    t,
    track,
    addNoticeBanner,
  ]);

  // ────────────────────────────────────────────────────────────────────

  return { signOutResponseData, isSigningOut, isSignedOut, signOut };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseSignOutProps = Pick<ReturnType<typeof useSignUp>, 'resetSignUpData'> &
  Pick<ReturnType<typeof useCustomerData>, 'customer'>;
